/**
 * Build-time configuration.
 *
 * Everything here is public, non-secret display configuration. The frontend
 * holds no credential and no OAuth client: sign-in is server-side, and the one
 * link the browser needs comes from `GET /api/v1/auth/config` at runtime.
 *
 * No bound, list or default of the product lives here. Those are published by
 * `GET /api/v1/config` and enforced by the server, so a deployment that raises
 * a number changes what people are told without a rebuilt image.
 */

/**
 * Who a person tells when the console cannot see secure storage.
 *
 * Shown on the screens that render *before* the server has answered — a
 * console whose API is down cannot tell the browser who to call, so this one
 * string has to be in the bundle. Everything reachable after the server
 * answers uses `access_contact` from `GET /api/v1/auth/config` instead, which
 * a deployment can change without rebuilding this image.
 */
export const SUPPORT_CHANNEL = "the platform team";

/**
 * Who the console acts as in the server's development mode.
 *
 * `AUTH_MODE=dev` authenticates nobody: it trusts an `X-User` header
 * (`docs/AUTH.md` §1), so the console sends one fixed name and every screen
 * carries the banner saying what that means. The default is the account the
 * dev stack also treats as an admin, so the approval screens work on a laptop
 * with no Google client.
 *
 * `VITE_DEV_USER` changes that name at dev-server start
 * (`make web VITE_DEV_USER=marie`). It exists because the approval gate has
 * two sides and one identity can only ever show you one of them. It is read
 * only when the server reports dev mode, and a production build of the API
 * ignores the header entirely.
 */
export const DEV_MODE_USERNAME = import.meta.env.VITE_DEV_USER || "admin";

/** Poll period for a dataset whose file is still arriving or being read. */
export const DATASET_ACTIVE_POLL_MS = 3_000;

/** Poll period for a dataset that has settled. */
export const DATASET_IDLE_POLL_MS = 30_000;

/** Poll period for the catalogue: rows move while somebody else uploads. */
export const CATALOGUE_POLL_MS = 20_000;

/** Poll period for the admin screens. */
export const ADMIN_POLL_MS = 15_000;

/** How long the published configuration stays fresh; it changes on a deploy. */
export const CONFIG_STALE_MS = 300_000;

/** How many audit lines the admin screen asks for. */
export const AUDIT_LIMIT = 200;

/** How many rows the read ledger asks for in one page. */
export const READS_PAGE_SIZE = 100;

/**
 * How much of a file the browser reads to offer the column names.
 *
 * The same 64 KB the server profiles with (`PROFILE_BYTES`), and it never
 * leaves the machine: it is read with the File API so question 4 can offer the
 * actual column names instead of asking somebody to type one.
 */
export const HEADER_SAMPLE_BYTES = 65_536;
